/**
 * ReferenceEdge — violet dashed reference link rendered on the ChatMap
 * when a P3_examine_then_dag squad cites a source message.
 *
 * Per cycle-2 UX F5: violet stroke (NOT amber — amber clashed with
 * shipped chatmap proposal-yellow vocabulary).
 *
 * Per SP3-MF-03: useTheme imports from `../../../../contexts/ThemeContext`
 * and destructures `darkMode` (NOT `isDark`).
 *
 * On hover the textPath label "references" appears centered on the
 * edge so operators can disambiguate without inspecting the underlying
 * node ids.
 */
import React, { useContext, useState } from 'react';
import { EdgeProps, MarkerType, getBezierPath, useReactFlow } from 'reactflow';
import { useTheme } from '../../../../contexts/ThemeContext';
import { PersonaChatUIContext } from '../../../../contexts/PersonaChatUIContext';


export interface ReferenceEdgeData {
  kind: 'reference';
  sourceNodeId: string;
  targetSpineRootId: string;
  squadId?: string;
}


export const ReferenceEdge: React.FC<EdgeProps<ReferenceEdgeData>> = ({
  id, sourceX, sourceY, targetX, targetY, data,
}) => {
  const { darkMode } = useTheme();
  // Violet per cycle-2 UX F5 (dark: lighter; light: deeper).
  const stroke = darkMode ? '#a78bfa' : '#8b5cf6';
  const [edgePath] = getBezierPath({ sourceX, sourceY, targetX, targetY });
  const [hovered, setHovered] = useState(false);

  // SP4 K.1 — click-to-jump. Pan chatmap to the source node + focus
  // the ChatStream bubble that produced it.
  const reactFlow = useReactFlow();
  const uiCtx = useContext(PersonaChatUIContext);
  const handleClick = () => {
    const srcId = data?.sourceNodeId;
    if (!srcId) return;
    const sourceNode = reactFlow.getNode(srcId);
    if (sourceNode) {
      reactFlow.setCenter(
        sourceNode.position.x,
        sourceNode.position.y,
        { duration: 300 },
      );
    }
    // Cross-panel: also focus the corresponding ChatStream bubble.
    uiCtx?.focusBubble?.(srcId);
  };

  return (
    <>
      <path
        id={id}
        className="react-flow__edge-path"
        d={edgePath}
        stroke={stroke}
        strokeWidth={1.5}
        strokeDasharray="6 4"
        fill="none"
        opacity={hovered ? 0.9 : 0.65}
        markerEnd={`url(#${MarkerType.ArrowClosed}-${stroke})`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
      />
      {hovered && (
        <text style={{ pointerEvents: 'none' }}>
          <textPath
            href={`#${id}`}
            startOffset="50%"
            textAnchor="middle"
            fontSize="9"
            fill={stroke}
          >
            references
          </textPath>
        </text>
      )}
    </>
  );
};
